import React from 'react';

import { cn } from '@/utils';

export type ChartScope = 'overall' | 'by_program';

export interface ScopeToggleOption<T extends string> {
  value: T;
  label: string;
}

interface ScopeToggleProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: ScopeToggleOption<T>[];
}

/** The Overall / By Program options every QC chart uses. */
export const OVERALL_BY_PROGRAM_OPTIONS: ScopeToggleOption<ChartScope>[] = [
  { value: 'overall', label: 'Overall' },
  { value: 'by_program', label: 'By Program' },
];

/**
 * Small segmented control used as the top-right slot on Score Trend, Radar,
 * and the rate-trend charts. Mirrors the `QcStatsTabs` shell pattern
 * (muted container + white "pill on white" active state with shadow) at a
 * smaller size so chart cards keep their hierarchy.
 */
export const ScopeToggle = <T extends string>({
  value,
  onChange,
  options,
}: ScopeToggleProps<T>): React.ReactElement => (
  <div
    role="tablist"
    aria-label="Chart scope"
    className="inline-flex items-center gap-1 rounded-lg bg-muted/60 p-0.5"
  >
    {options.map(option => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors',
            active
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-background/70 hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);
